import React, { useEffect, useRef, useState } from 'react';
import { DARK_BASE, TEAL } from './constants';

interface FilterDropdownProps {
  label: string;
  options: string[];
  selected: string[];
  onToggle: (value: string) => void;
  onClear: () => void;
}

const FilterDropdown: React.FC<FilterDropdownProps> = ({
  label,
  options,
  selected,
  onToggle,
  onClear,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const isAll = selected.length === 0 || selected.length === options.length;

  const displayLabel = isAll
    ? label
    : selected.length === 1
    ? selected[0]
    : `${selected.length} selected`;

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        className="btn btn-sm"
        onClick={() => setOpen(o => !o)}
        style={{
          background: isAll ? 'rgba(255,255,255,0.12)' : TEAL,
          color: isAll ? '#fff' : DARK_BASE,
          border: '1px solid rgba(255,255,255,0.25)',
          borderRadius: 20,
          padding: '4px 14px',
          fontSize: 13,
          fontWeight: 500,
          whiteSpace: 'nowrap',
          display: 'flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {displayLabel}
        <svg width="10" height="6" viewBox="0 0 10 6">
          <path
            d="M1 1l4 4 4-4"
            stroke="currentColor"
            strokeWidth="1.5"
            fill="none"
            strokeLinecap="round"
          />
        </svg>
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            right: 0,
            top: 'calc(100% + 6px)',
            background: 'var(--bs-body-bg)',
            border: '1px solid var(--bs-border-color)',
            borderRadius: 'var(--bs-border-radius)',
            minWidth: 200,
            maxHeight: 300,
            overflowY: 'auto',
            boxShadow: '0 4px 20px rgba(30,22,77,0.15)',
            zIndex: 1000,
            padding: '6px 0',
          }}
        >
          <button
            className="btn btn-sm w-100 text-start px-3 py-1"
            style={{ color: 'var(--bs-body-color)', fontSize: 13 }}
            onClick={() => { onClear(); setOpen(false); }}
          >
            All {label.replace(/^All /, '')}
          </button>
          <div style={{ borderTop: '1px solid var(--bs-border-color)', margin: '4px 0' }} />
          {options.map(opt => (
            <label
              key={opt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                padding: '5px 12px',
                cursor: 'pointer',
                fontSize: 13,
                color: 'var(--bs-body-color)',
              }}
            >
              <input
                type="checkbox"
                checked={selected.includes(opt)}
                onChange={() => onToggle(opt)}
                style={{ accentColor: TEAL }}
              />
              {opt}
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default FilterDropdown;
